import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../contexts/hooks';
import { NoDataPanel, newToast } from '../../..';
import { formatMessage } from 'devextreme/localization';
import { useApiTracking } from '../../../../api/api.tracking';
import { TrackingProgressBar } from '../../../utils/tracking-progress-bar/TrackingProgressBar';
import { Button, LoadPanel, ScrollView, Toolbar, Tooltip } from 'devextreme-react';
import { Item } from 'devextreme-react/toolbar';
import { shallowEqual } from 'react-redux';
import { setPanelPined, setPanelVisible } from '../../../../contexts/slices/orders';
import { Pin, resButtonRefresh, resGridStatus3, UnPin } from '../../../../images/icons';
import { useScreenSize } from '../../../../utils/media-query';
import ConfirmationPopup from '../../../utils/popup-confirmation/PopupConfirmation';
import { useActiveTab } from '../../../../hooks/useActiveTab';
import './OrdTracking.scss';

const Index = () => {
  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder);
  
  // Hook para verificar si esta tab está activa (tab 5 = Tracking)
  const isActiveTab = useActiveTab(5);
  
  const { getTracking, getAllStates, setTracking } = useApiTracking();
  const [trackingData, setTrackingData] = useState<any[]>([]);
  const [allStates, setAllStates] = useState<any[]>([]);
  const [highlightedSampleId, setHighlightedSampleId] = useState<number | null>(null);
  const [isTracking, setIsTracking] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const panelVisible = useAppSelector((x) => x.globalOrders.PanelVisible, shallowEqual);
  const dispatch = useAppDispatch();
  const { isSmall, isXSmall } = useScreenSize();

  // Cargar todos los estados disponibles al iniciar
  useEffect(() => {
    getAllStates().then((res) => {
      if (res && Array.isArray(res)) {
        setAllStates(res.sort((a, b) => a.Tke_Orden - b.Tke_Orden));
      }
    });
  }, []);

  // Cargar tracking cuando cambia el idOrd Y esta tab está activa
  useEffect(() => {
    // Solo consultar si esta tab está activa
    if (isActiveTab && idOrd && idOrd !== -1) {
      fetchTrackingData();
    } else if (!isActiveTab) {
      // Si la tab no está activa, limpiar datos para evitar mostrar datos antiguos
      setTrackingData([]);
    }
  }, [idOrd, isActiveTab]);

  const fetchTrackingData = useCallback(() => {
    if (!idOrd || idOrd === -1) return;

    setIsLoading(true);
    getTracking(idOrd, undefined, undefined, undefined, undefined)
      .then((res) => {
        if (res && Array.isArray(res)) {
          setTrackingData(res);
        } else {
          setTrackingData([]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [idOrd, getTracking]);

  const handleAdvanceSample = async (orderId: number, idTk: number, tkeId: number) => {
    setIsTracking(true);

    const trackingUpdate = [{ IdTk: idTk, IdOrd: orderId, IdTkEst: tkeId }];

    try {
      const response = await setTracking(trackingUpdate);
      if (response.length > 0) {
        setTrackingData(response);
        newToast('SUCCESS', 'Tracking actualizado correctamente', 3);
        setHighlightedSampleId(idTk);
        setTimeout(() => {
          setHighlightedSampleId(null);
        }, 800);
      }
    } finally {
      setIsTracking(false);
    }
  };

  const handleRejectSample = (orderId: number, tmId: number) => {
    // La lógica de rechazo se maneja dentro de TrackingProgressBar
  };

  const handleValidateAllSamples = () => {
    if (!trackingData || trackingData.length === 0 || allStates.length === 0) {
      newToast('WARNING', 'No hay muestras para validar', 3);
      return;
    }

    const lastState = allStates[allStates.length - 1];
    const samplesToAdvance = trackingData.filter((tracking) => tracking.Tk_Tke_Id !== null && tracking.Tk_Tke_Id !== lastState.Tke_Id);

    if (samplesToAdvance.length === 0) {
      newToast('INFO', 'Todas las muestras han finalizado el proceso de tracking o están descartadas', 3);
      return;
    }

    setConfirmModalVisible(true);
  };

  const handleConfirmValidateAll = async () => {
    setConfirmModalVisible(false);

    if (!trackingData || trackingData.length === 0 || allStates.length === 0) {
      return;
    }

    const lastState = allStates[allStates.length - 1];
    const samplesToAdvance = trackingData.filter((tracking) => tracking.Tk_Tke_Id !== null && tracking.Tk_Tke_Id !== lastState.Tke_Id);

    if (samplesToAdvance.length === 0) {
      return;
    }

    setIsTracking(true);

    try {
      const trackingUpdates = samplesToAdvance.map((tracking) => ({
        IdTk: tracking.Tk_Id,
        IdOrd: tracking.Tk_Ord_Id,
        IdTkEst: tracking.Tk_Tke_Id,
      }));

      const response = await setTracking(trackingUpdates);

      if (response && response.length > 0) {
        setTrackingData(response);
        newToast('SUCCESS', `${samplesToAdvance.length} muestra(s) avanzada(s) correctamente`, 3);
      }
    } catch (error) {
      newToast('ERROR', 'Error al validar las muestras', 3);
    } finally {
      setIsTracking(false);
    }
  };

  const handleCancelValidateAll = () => {
    setConfirmModalVisible(false);
  };

  const handlePanelToggle = () => {
    dispatch(setPanelVisible(!panelVisible));
    dispatch(setPanelPined(false));
  };

  if (!idOrd || idOrd === -1) {
    return <NoDataPanel title={formatMessage('OrdNoDataTitle')} description={formatMessage('OrdNoDataDescription')} />;
  }

  return (
    <div className='ord-tracking-container'>
      {/* Toolbar */}
      <div className='ord-tracking-toolbar'>
        <Toolbar>
          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='ValidarResultTracking' icon={resGridStatus3} text='Validar resultado' onClick={handleValidateAllSamples} stylingMode='text' />
            <Tooltip target={'#ValidarResultTracking'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Confirmar todas las muestras
            </Tooltip>
          </Item>

          <Item location='after' locateInMenu='auto'>
            <div className='separator' />
          </Item>

          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='RefrescarTracking' icon={resButtonRefresh} text='Actualizar' onClick={fetchTrackingData} stylingMode='text' />
            <Tooltip target={'#RefrescarTracking'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </Item>

          <Item location='after' locateInMenu='never' showText='inMenu' widget='dxButton'>
            <Button id='showpanelTracking' icon={panelVisible ? Pin : UnPin} text={panelVisible ? 'Panel Visible' : 'Panel oculto'} stylingMode='text' onClick={handlePanelToggle} />
            <Tooltip target={'#showpanelTracking'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              {panelVisible ? 'Ocultar panel' : 'Abrir panel'}
            </Tooltip>
          </Item>
        </Toolbar>
      </div>

      {/* Contenido del tracking */}
      <div className='ord-tracking-content'>
        <ScrollView height='100%' style={{ paddingBottom: isSmall || isXSmall ? `150px` : `50px` }}>
          <TrackingProgressBar trackingData={trackingData} allStates={allStates} currentOrder={{ Ord_Id: idOrd }} onAdvance={handleAdvanceSample} onReject={handleRejectSample} onTrackingUpdate={setTrackingData} highlightedSampleId={highlightedSampleId} />
        </ScrollView>
      </div>

      {/* Modal de confirmación */}
      {confirmModalVisible && (
        <ConfirmationPopup
          visible={confirmModalVisible}
          onConfirm={handleConfirmValidateAll}
          onCancel={handleCancelValidateAll}
          message={`Se avanzarán ${trackingData.filter((tracking) => tracking.Tk_Tke_Id !== null && tracking.Tk_Tke_Id !== allStates[allStates.length - 1]?.Tke_Id).length} muestra(s) al siguiente estado`}
          loading={isTracking}
        />
      )}

      {/* Indicador de carga para consulta */}
      <LoadPanel visible={isLoading} showPane={false} position={{ of: '.ord-tracking-content' }} />

      {/* Indicador de carga para actualizaciones */}
      <LoadPanel visible={isTracking} showPane={false} position={{ of: '.ord-tracking-content' }} />
    </div>
  );
};

export default Index;
